import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Tag, Plus, Edit2, Trash2, ToggleLeft, ToggleRight,
  Percent, DollarSign, Calendar, Clock, Users, TrendingUp, X
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// Display a YYYY-MM-DD or ISO date in the user's local calendar without
// the UTC-midnight off-by-one. e.g. "2026-05-12" → "5/12/2026"
const formatLocalDate = (s) => {
  if (!s) return '';
  const datePart = s.includes('T') ? s.split('T')[0] : s;
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString();
};

const AdminPromotions = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [redemptionsOpen, setRedemptionsOpen] = useState(false);
  const [redemptionsData, setRedemptionsData] = useState(null);
  const [redemptionsLoading, setRedemptionsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    promo_code: '',
    valid_from: '',
    valid_until: '',
    valid_days: 'Ongoing',
    active_from_time: '',
    active_until_time: '',
    max_uses: '',
    active: true
  });

  // Check admin access
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    } else if (currentUser.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Fetch promotions
  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/promotions/');
      setPromotions(response.data);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchPromotions();
    }
  }, [currentUser]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      promo_code: '',
      valid_from: '',
      valid_until: '',
      valid_days: 'Ongoing',
      active_from_time: '',
      active_until_time: '',
      max_uses: '',
      active: true
    });
    setEditingPromo(null);
  };

  const handleCreate = async () => {
    try {
      // Basic time-window validation on the client
      const from = formData.active_from_time;
      const until = formData.active_until_time;
      if ((from && !until) || (!from && until)) {
        toast.error('If you set a time window, please fill in BOTH the start and end times.');
        return;
      }
      const payload = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        active_from_time: formData.active_from_time || null,
        active_until_time: formData.active_until_time || null,
      };

      await api.post('/api/promotions/', payload);
      toast.success('Promotion created successfully!');
      setShowCreateDialog(false);
      resetForm();
      fetchPromotions();
    } catch (error) {
      console.error('Error creating promotion:', error);
      toast.error(error.response?.data?.detail || 'Failed to create promotion');
    }
  };

  const handleUpdate = async () => {
    try {
      const from = formData.active_from_time;
      const until = formData.active_until_time;
      if ((from && !until) || (!from && until)) {
        toast.error('If you set a time window, please fill in BOTH the start and end times.');
        return;
      }
      const payload = {};

      if (formData.title) payload.title = formData.title;
      if (formData.description) payload.description = formData.description;
      if (formData.discount_type) payload.discount_type = formData.discount_type;
      if (formData.discount_value) payload.discount_value = parseFloat(formData.discount_value);
      if (formData.promo_code) payload.promo_code = formData.promo_code;
      // Always send these so admins can CLEAR them (empty = make available now / forever)
      payload.valid_from = formData.valid_from || null;
      payload.valid_until = formData.valid_until || null;
      payload.valid_days = formData.valid_days || null;
      payload.active_from_time = formData.active_from_time || null;
      payload.active_until_time = formData.active_until_time || null;
      payload.max_uses = formData.max_uses ? parseInt(formData.max_uses) : null;
      payload.active = formData.active;

      await api.put(`/api/promotions/${editingPromo.id}`, payload);
      toast.success('Promotion updated successfully!');
      setShowCreateDialog(false);
      resetForm();
      fetchPromotions();
    } catch (error) {
      console.error('Error updating promotion:', error);
      toast.error(error.response?.data?.detail || 'Failed to update promotion');
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;

    try {
      await api.delete(`/api/promotions/${promoId}`);
      toast.success('Promotion deleted');
      fetchPromotions();
    } catch (error) {
      console.error('Error deleting promotion:', error);
      toast.error('Failed to delete promotion');
    }
  };

  const handleToggle = async (promoId) => {
    try {
      const response = await api.post(`/api/promotions/${promoId}/toggle`);
      toast.success(response.data.message);
      fetchPromotions();
    } catch (error) {
      console.error('Error toggling promotion:', error);
      toast.error('Failed to toggle promotion status');
    }
  };

  const openRedemptions = async (promo) => {
    setRedemptionsOpen(true);
    setRedemptionsLoading(true);
    setRedemptionsData({ promo_code: promo.promo_code, title: promo.title, redemptions: [] });
    try {
      const res = await api.get(`/api/promotions/${promo.id}/redemptions`);
      setRedemptionsData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load users');
      setRedemptionsOpen(false);
    } finally {
      setRedemptionsLoading(false);
    }
  };


  const openEditDialog = (promo) => {
    setEditingPromo(promo);
    setFormData({
      title: promo.title,
      description: promo.description,
      discount_type: promo.discount_type,
      discount_value: promo.discount_value.toString(),
      promo_code: promo.promo_code,
      valid_from: promo.valid_from || '',
      valid_until: promo.valid_until || '',
      valid_days: promo.valid_days || 'Ongoing',
      active_from_time: promo.active_from_time || '',
      active_until_time: promo.active_until_time || '',
      max_uses: promo.max_uses ? promo.max_uses.toString() : '',
      active: promo.active
    });
    setShowCreateDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-lime-400 border-t-transparent mx-auto mb-4"></div>
          <p className="text-zinc-400">Loading promotions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-20">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">
              <Tag className="inline-block w-10 h-10 mr-3 text-lime-400" />
              Promotion Management
            </h1>
            <p className="text-zinc-400">Create and manage discount codes and special offers</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowCreateDialog(true);
            }}
            className="mt-4 md:mt-0 bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Promotion
          </Button>
        </div>

        {/* Popup-reset shortcut */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border border-purple-500/30 bg-purple-500/5">
          <p className="text-sm text-zinc-300">
            <span className="font-semibold text-purple-300">🔄 Re-pop promos for everyone:</span>{' '}
            <span className="text-zinc-400">Use this after launching a new campaign so every customer sees the popup again on their next login.</span>
          </p>
          <Button
            size="sm"
            variant="outline"
            className="border-purple-500/50 text-purple-200 hover:bg-purple-500/10 flex-shrink-0"
            onClick={async () => {
              if (!window.confirm('Reset the "promo seen" state for ALL customers?\n\nEveryone will see the active promo popup on their next login.')) return;
              try {
                const r = await api.post('/api/promotions/popup/reset-all');
                toast.success(`Reset ${r.data.users_reset} user(s). Popups will re-show on next login.`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Reset failed');
              }
            }}
            data-testid="reset-popup-seen-btn"
          >
            Reset popup history
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Total Promotions</p>
                  <p className="text-3xl font-bold text-white">{promotions.length}</p>
                </div>
                <Tag className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Active</p>
                  <p className="text-3xl font-bold text-lime-400">
                    {promotions.filter(p => p.active).length}
                  </p>
                </div>
                <ToggleRight className="w-8 h-8 text-lime-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Total Uses</p>
                  <p className="text-3xl font-bold text-white">
                    {promotions.reduce((sum, p) => sum + (p.current_uses || 0), 0)}
                  </p>
                </div>
                <Users className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Inactive</p>
                  <p className="text-3xl font-bold text-zinc-500">
                    {promotions.filter(p => !p.active).length}
                  </p>
                </div>
                <ToggleLeft className="w-8 h-8 text-zinc-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Promotions List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {promotions.map(promo => (
            <Card key={promo.id} className={`${
              promo.active ? 'bg-zinc-900 border-lime-500' : 'bg-zinc-900/50 border-zinc-800'
            }`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                      {promo.title}
                      {promo.active ? (
                        <Badge className="bg-lime-500 text-black">Active</Badge>
                      ) : (
                        <Badge className="bg-zinc-700 text-zinc-400">Inactive</Badge>
                      )}
                    </CardTitle>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-lime-400">
                      {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `$${promo.discount_value}`}
                    </div>
                    <div className="text-xs text-zinc-500">OFF</div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-zinc-300">{promo.description}</p>

                <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
                  <p className="text-zinc-500 text-xs mb-1">Promo Code:</p>
                  <p className="text-lime-400 font-mono text-lg font-bold">{promo.promo_code}</p>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-zinc-500">Valid Days</p>
                    <p className="text-white">{promo.valid_days || 'Ongoing'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Uses</p>
                    <p className="text-white">
                      {promo.current_uses || 0}
                      {promo.max_uses ? ` / ${promo.max_uses}` : ' / ∞'}
                    </p>
                  </div>
                </div>

                {(promo.valid_from || promo.valid_until) && (
                  <div className="flex items-center text-xs text-zinc-400">
                    <Calendar className="w-4 h-4 mr-1" />
                    {promo.valid_from && formatLocalDate(promo.valid_from)}
                    {promo.valid_from && promo.valid_until && ' - '}
                    {promo.valid_until && formatLocalDate(promo.valid_until)}
                  </div>
                )}

                {(promo.active_from_time || promo.active_until_time) && (
                  <div className="flex items-center text-xs text-lime-400" data-testid={`promo-time-window-${promo.id}`}>
                    <Clock className="w-4 h-4 mr-1" />
                    {promo.active_from_time || '00:00'} – {promo.active_until_time || '23:59'} ET
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => handleToggle(promo.id)}
                    variant="outline"
                    size="sm"
                    className={`flex-1 ${
                      promo.active
                        ? 'border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black'
                        : 'border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black'
                    }`}
                  >
                    {promo.active ? (
                      <>
                        <ToggleLeft className="w-4 h-4 mr-1" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <ToggleRight className="w-4 h-4 mr-1" />
                        Activate
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => openEditDialog(promo)}
                    variant="outline"
                    size="sm"
                    className="border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-black"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => openRedemptions(promo)}
                    variant="outline"
                    size="sm"
                    className="border-cyan-400 text-cyan-300 hover:bg-cyan-500/15"
                    data-testid={`promo-users-btn-${promo.id}`}
                  >
                    Users ({promo.current_uses || 0})
                  </Button>
                  <Button
                    onClick={() => handleDelete(promo.id)}
                    variant="outline"
                    size="sm"
                    className="border-red-400 text-red-400 hover:bg-red-400 hover:text-black"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {promotions.length === 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-12 text-center">
              <Tag className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-semibold mb-2">No Promotions Yet</h3>
              <p className="text-zinc-400 mb-6">Create your first promotion to start offering discounts!</p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Promotion
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lime-400">
                {editingPromo ? 'Edit Promotion' : 'Create New Promotion'}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {editingPromo ? 'Update promotion details' : 'Set up a new discount code or special offer'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Tuesday Special"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="e.g., $5 off any haircut service"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Discount Type *</Label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md p-2"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>

                <div>
                  <Label>Discount Value *</Label>
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                    placeholder={formData.discount_type === 'percentage' ? '15' : '10'}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div>
                <Label>Promo Code *</Label>
                <Input
                  value={formData.promo_code}
                  onChange={(e) => setFormData({...formData, promo_code: e.target.value.toUpperCase()})}
                  placeholder="e.g., TUESDAY5"
                  className="bg-zinc-800 border-zinc-700 text-white font-mono"
                />
              </div>

              <div>
                <Label>Valid Days</Label>
                <Input
                  value={formData.valid_days}
                  onChange={(e) => setFormData({...formData, valid_days: e.target.value})}
                  placeholder="e.g., Every Tuesday, Ongoing"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valid From (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>

                <div>
                  <Label>Valid Until (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              {/* Time-of-day window (Eastern Time) */}
              <div className="pt-2 border-t border-zinc-800">
                <Label className="text-lime-400">
                  ⏰ Active hours (optional, Eastern Time)
                </Label>
                <p className="text-xs text-zinc-500 mb-2">
                  Only valid when a customer books during this window each day. Leave empty for all-day.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-zinc-400">From</Label>
                    <Input
                      type="time"
                      value={formData.active_from_time}
                      onChange={(e) => setFormData({...formData, active_from_time: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="promo-active-from-time"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Until</Label>
                    <Input
                      type="time"
                      value={formData.active_until_time}
                      onChange={(e) => setFormData({...formData, active_until_time: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="promo-active-until-time"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Max Uses (Optional)</Label>
                <Input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                  placeholder="Leave empty for unlimited"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="active" className="cursor-pointer">Active (visible to customers)</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
                className="border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                onClick={editingPromo ? handleUpdate : handleCreate}
                className="bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700"
              >
                {editingPromo ? 'Update Promotion' : 'Create Promotion'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Redemptions Modal */}
        {redemptionsOpen && (
          <div
            onClick={() => setRedemptionsOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
              zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
            data-testid="redemptions-modal"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-zinc-900 border border-cyan-500/40 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Who used {redemptionsData?.promo_code || ''}?
                  </h3>
                  <p className="text-zinc-400 text-sm mt-1">
                    {redemptionsData?.redemption_count ?? 0} uses · {redemptionsData?.unique_customers ?? 0} unique customers · ${(redemptionsData?.total_discount_value ?? 0).toFixed(2)} total discount given
                  </p>
                </div>
                <button
                  onClick={() => setRedemptionsOpen(false)}
                  className="text-zinc-500 hover:text-white"
                  aria-label="Close"
                  data-testid="redemptions-close-btn"
                >
                  ✕
                </button>
              </div>

              {redemptionsLoading ? (
                <p className="text-center text-zinc-500 py-10">Loading…</p>
              ) : !redemptionsData?.redemptions || redemptionsData.redemptions.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-zinc-400">No one's used this promo code yet.</p>
                  <p className="text-zinc-600 text-sm mt-1">Share the code with customers to get redemptions.</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {redemptionsData.redemptions.map((r) => (
                    <div key={r.appointment_id} className="py-3" data-testid={`redemption-${r.appointment_id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-white">{r.customer_name}</p>
                          <p className="text-xs text-zinc-500 mt-0.5">
                            {r.customer_email && <span>{r.customer_email}</span>}
                            {r.customer_phone && <span> · {r.customer_phone}</span>}
                          </p>
                          <p className="text-xs text-zinc-400 mt-1">
                            {r.service_name} · {r.date} {r.time}
                            <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase ${
                              r.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                              r.status === 'cancelled' ? 'bg-red-500/20 text-red-300' :
                              'bg-zinc-700 text-zinc-300'
                            }`}>
                              {r.status}
                            </span>
                          </p>
                        </div>
                        <p className="text-lime-400 font-bold text-sm whitespace-nowrap">
                          -${(r.discount_amount || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPromotions;